import cloneDeep from 'lodash-es/cloneDeep';
import map from 'lodash-es/map';
import flatten from 'lodash-es/flatten';
import intersection from 'lodash-es/intersection';
import uniq from 'lodash-es/uniq';
import capitalize from 'lodash-es/capitalize';
import format from 'date-fns/format';
import addDays from 'date-fns/addDays';

/**
 * Prepares locations list
 *
 * @param {string[]} locations
 * @param {string[]} assignmentAreas
 * @param {{name: string, locations: {name: string}[]}[]} allAssignmentAreas
 * @returns {string[]}
 */
export function prepareLocationsList(locations, assignmentAreas, allAssignmentAreas) {
    let selectedAssignmentAreas = allAssignmentAreas.filter(aa => assignmentAreas.includes(aa.name));
    let allLocations = flatten(map(selectedAssignmentAreas, 'locations'));
    let allLocationNames = uniq(map(allLocations, 'name'));

    return locations.length > 0 ? intersection(locations, allLocationNames) : allLocationNames;
}

/**
 * Prepares flat configuration object for widget.
 * Maps assignment areas, locations, and call types to arrays of strings (numbers).
 *
 * @param {object} configuration
 * @param {{name: string, locations: {name: string}[]}[]} allAssignmentAreas
 * @param {{id: number}[]} allCallTypes
 * @returns {object}
 */
export function prepareWidgetConfiguration(configuration, allAssignmentAreas, allCallTypes) {
    let result = cloneDeep(configuration);

    result.name = configuration.name.trim();

    result.aa =
        Array.isArray(configuration.aa) && configuration.aa.length > 0
            ? configuration.aa
            : map(allAssignmentAreas, 'name');

    if (configuration.locations) {
        result.locations = prepareLocationsList(configuration.locations, result.aa, allAssignmentAreas);
    }

    if (Array.isArray(configuration.call_types)) {
        result.call_types =
            configuration.call_types.length > 0 ? configuration.call_types : map(allCallTypes, 'id');
    }

    return result;
}

/**
 * Translates the list of options
 * @param {Function} translate
 * @param {{ value: number, name: string}[]} list
 * @returns {{ value: number, name: string}[]}
 */
export function translateOptions(translate, list) {
    return list.map(({ value, name }) => ({ value, name: translate(name) }));
}

/**
 * Finds option in given options list by value and returns its name or undefined if nothing found
 * @param {{ value: number, name: string}[]} list
 * @param {number} value
 * @returns {string|undefined}
 */
export function getTranslationKeyByOption(list, value) {
    let foundItem = list.find(item => item.value === value);

    return (foundItem || {}).name;
}

/**
 * Transforms array of strings to array of options for Multiselect component
 * @param {string[]} data
 * @returns {{ name: string, value: string}[]}
 */
export function transformArrayToOptions(data) {
    return data.map(value => ({ name: value, value }));
}

/**
 * Transforms array of options for Multiselect back to array of strings
 * @param {{ name: string, value: string}[]} data
 * @returns {string[]}
 */
export function transformOptionsToArray(data) {
    return data.map(item => item.value);
}

export const periods = [
    { value: 1, name: 'dashboard.labels.currentDay' },
    { value: 3, name: 'dashboard.labels.threeDays' },
    { value: 7, name: 'dashboard.labels.week' },
    { value: 30, name: 'dashboard.labels.month' },
];

export const durations = [
    { value: 0, name: 'dashboard.labels.allEvents' },
    { value: 1, name: 'dashboard.labels.notOnTime' },
    { value: 2, name: 'dashboard.labels.late' },
];

export const nurseCalls = [
    { value: 0, name: 'dashboard.labels.allNurseCalls' },
    { value: 1, name: 'reporting.callTypes.observe' },
    { value: 2, name: 'reporting.callTypes.assistance' },
    { value: 3, name: 'reporting.callTypes.critical' },
];

export const processing = [
    { value: 1, name: 'dashboard.labels.response' },
    { value: 2, name: 'dashboard.labels.duration' },
];

/**
 * Converts snake_case_string to SnakeCaseString
 * @param {string} str
 * @returns {string}
 */
export function convertSnakeToCamelString(str) {
    return str
        .split('_')
        .map(capitalize)
        .join('');
}

/**
 * Creates updater functions for widget configuration forms
 * Transform field name "some_field" into method "updateSomeField"
 * @param {string[]} fields
 * @returns {Object}
 */
export function createUpdaters(...fields) {
    let result = {};
    fields.forEach(field => {
        let method = 'update' + convertSnakeToCamelString(field);
        result[method] = function(value) {
            this.$emit('input', Object.assign({}, this.value, { [field]: value }));
        };
    });

    return result;
}

/**
 * Returns call type name or empty string if it's not found
 * @param {{ id: number, name: string }[]} callTypes
 * @param {number} id
 * @returns {string}
 */
export function getCallTypeNameById(callTypes, id) {
    return (callTypes.find(ct => ct.id === id) || {}).name || '';
}

/**
 * Returns array with dates of seven days starting from "date" and to the past
 * @param {Date} date
 * @returns {string[]} array of dates in format like '05-Dec'
 */
export function getPreviousWeekArray(date) {
    let list = [];

    for (let i = -6; i <= 0; i++) {
        list.push(format(addDays(date, i), 'dd-MMM'));
    }

    return list;
}
